import { apiClient, ApiResponse } from '../api.client';
import { Stay, StaysListResponse, StayResponse } from './types';

export type { Stay, StaysListResponse, StayResponse };

export interface ListStaysParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  stars?: number;
  min_rating?: number;
  city?: string;
  country?: string;
  is_active?: boolean;
  sort_by?: string;
  sort_dir?: 'ASC' | 'DESC';
}

export async function getStays(params: ListStaysParams = {}): Promise<StaysListResponse> {
  const queryParams: Record<string, any> = {
    page: params.page || 1,
    limit: params.limit || 20,
    sort_by: params.sort_by || 'created_at',
    sort_dir: params.sort_dir || 'DESC',
  };

  if (params.search) queryParams.search = params.search;
  if (params.type) queryParams.type = params.type;
  if (params.stars) queryParams.stars = params.stars;
  if (params.min_rating) queryParams.min_rating = params.min_rating;
  if (params.city) queryParams.city = params.city;
  if (params.country) queryParams.country = params.country;
  if (params.is_active !== undefined) queryParams.is_active = params.is_active ? 1 : 0;

  const response = await apiClient.get<Stay[]>(`/api/stays`, queryParams);
  return response as unknown as StaysListResponse;
}

export interface Destination {
  city: string;
  country: string;
}

export async function getDestinations(): Promise<Destination[]> {
  const response = await apiClient.get<Destination[]>(`/api/stays/destinations`);
  return (response as any)?.data || [];
}

export async function getStayById(stayId: string): Promise<StayResponse> {
  const response = await apiClient.get<Stay>(`/api/stays/${stayId}`);
  return response as unknown as StayResponse;
}

export async function createStay(stay: Partial<Stay>): Promise<StayResponse> {
  const response = await apiClient.post<Stay>('/api/stays', stay);
  return response as unknown as StayResponse;
}

export async function updateStay(stayId: string, stay: Partial<Stay>): Promise<StayResponse> {
  const response = await apiClient.put<Stay>(`/api/stays/${stayId}`, stay);
  return response as unknown as StayResponse;
}

export async function updateStayStatus(stayId: string, is_active: boolean): Promise<StayResponse> {
  const response = await apiClient.patch<Stay>(`/api/stays/${stayId}/status`, { is_active: is_active ? 1 : 0 });
  return response as unknown as StayResponse;
}

export async function deleteStay(stayId: string): Promise<void> {
  await apiClient.delete(`/api/stays/${stayId}`);
}

export interface RoomParams {
  page?: number;
  limit?: number;
  status?: string;
  room_type?: string;
  sort_by?: string;
  sort_dir?: 'ASC' | 'DESC';
}

export async function getRooms(stayId: string, params: RoomParams = {}): Promise<any> {
  const queryParams: Record<string, any> = {
    page: params.page || 1,
    limit: params.limit || 50,
  };

  if (params.status) queryParams.status = params.status;
  if (params.room_type) queryParams.room_type = params.room_type;

  const response = await apiClient.get<any>(`/api/stays/${stayId}/rooms`, queryParams);
  return response;
}

export async function createRoom(stayId: string, room: any): Promise<any> {
  const response = await apiClient.post(`/api/stays/${stayId}/rooms`, room);
  return response;
}

export async function updateRoom(roomId: string, room: any): Promise<any> {
  const response = await apiClient.put(`/api/rooms/${roomId}`, room);
  return response;
}

export async function updateRoomStatus(roomId: string, status: string): Promise<any> {
  const response = await apiClient.patch(`/api/rooms/${roomId}/status`, { status });
  return response;
}

export async function deleteRoom(roomId: string): Promise<void> {
  await apiClient.delete(`/api/rooms/${roomId}`);
}

export async function getAmenities(params: { category?: string; is_active?: boolean } = {}): Promise<any> {
  const queryParams: Record<string, any> = {};
  if (params.category) queryParams.category = params.category;
  if (params.is_active !== undefined) queryParams.is_active = params.is_active ? 1 : 0;

  const response = await apiClient.get('/api/amenities', queryParams);
  return response;
}

export async function createAmenity(amenity: any): Promise<any> {
  const response = await apiClient.post('/api/amenities', amenity);
  return response;
}

export async function updateAmenity(amenityId: string, amenity: any): Promise<any> {
  const response = await apiClient.put(`/api/amenities/${amenityId}`, amenity);
  return response;
}

// Stay Availability Types and Functions (stub - backend not yet implemented)
export enum StayAvailabilityStatus {
  AVAILABLE = 'available',
  LIMITED = 'limited',
  FULLY_BOOKED = 'fully_booked',
}

export interface StayAvailability {
  stayId: string;
  stayName: string;
  checkIn: string;
  checkOut: string;
  overallStatus: StayAvailabilityStatus;
  totalRooms: number;
  nightAvailability: {
    date: string;
    totalRooms: number;
    bookedRooms: number;
    availableRooms: number;
    status: StayAvailabilityStatus;
  }[];
}

export async function checkStayAvailability(
  stayId: string,
  checkIn: string,
  checkOut: string
): Promise<ApiResponse<StayAvailability>> {
  // Stub: return mock data until backend is implemented
  const nights = [];
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  let current = new Date(start);

  while (current < end) {
    nights.push({
      date: current.toISOString().split('T')[0],
      totalRooms: 10,
      bookedRooms: Math.floor(Math.random() * 8),
      availableRooms: Math.max(0, 10 - Math.floor(Math.random() * 8)),
      status: StayAvailabilityStatus.AVAILABLE,
    });
    current.setDate(current.getDate() + 1);
  }

  const availability: StayAvailability = {
    stayId,
    stayName: 'Stay',
    checkIn,
    checkOut,
    overallStatus: StayAvailabilityStatus.AVAILABLE,
    totalRooms: 10,
    nightAvailability: nights,
  };

  return { status: 'success', code: 200, message: 'OK', data: availability } as ApiResponse<StayAvailability>;
}
